import {
    Box,
    Button,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    MenuList,
} from "@mui/material";
import { ChevronLeft, Language, Lock, ShareOutlined } from "@mui/icons-material";
import {
    useRef,
    useState
} from "react";

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { ShareIcon } from "../icons";
import { staticDashboardService } from "../../lib/dashboard/StaticDashboardService";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { useSnackbar } from "notistack";

type ShareButtonProps = {
    linkMessage: string;
};

interface RouteParams {
    propertyId: string;
}

enum ShareMethod {
    PRIVATE = "Private",
    PUBLIC = "Public"
};

export const ShareButton = ({ linkMessage }: ShareButtonProps) => {
    const { propertyId } = useParams<RouteParams>();
    const PRIVATE_LINK = window.location.href;
    const PUBLIC_LINK = `${window.location.origin}/public/dashboard/property/${propertyId}`;
    const { data: staticDashboardData, isLoading } = useQuery({
        queryKey: ["staticDashboard", propertyId],
        queryFn: async () => {
            return await staticDashboardService.get(propertyId);
        },
        enabled: !!propertyId
    });

    const snackbar = useSnackbar();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [shareMethod, setShareMethod] = useState<ShareMethod | null>(null);
    const anchorRef = useRef<HTMLButtonElement | null>(null);

    const copyLink = async () => {
        if (shareMethod === ShareMethod.PRIVATE) {
            await navigator.clipboard.writeText(PRIVATE_LINK);
        } else {
            await navigator.clipboard.writeText(PUBLIC_LINK);
        }

        snackbar.enqueueSnackbar('Link copied to clipboard', { variant: 'success' });
        setIsDialogOpen(false);
    };

    const shareLink = async () => {
        try {
            await navigator.share({
                title: linkMessage,
                url: shareMethod === ShareMethod.PRIVATE ? PRIVATE_LINK : PUBLIC_LINK,
            });
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
            // ignore error
        }
    };

    return (
        <>
            <Box>
                <Button
                    ref={anchorRef}
                    variant="outlined"
                    color="inherit"
                    onClick={() => {
                        setIsDialogOpen(true);
                        setShareMethod(null);
                    }}
                    startIcon={<ShareIcon />}
                    sx={{ textTransform: "none" }}
                >
                    Share View
                </Button>
                <Menu
                    open={isDialogOpen}
                    anchorEl={anchorRef.current}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right"
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right"
                    }}
                    onClose={(): void => {
                        setIsDialogOpen(false);
                    }}
                    sx={{
                        marginTop: "8px",
                        right: "0"
                    }}
                >
                    <Box
                        width="200px"
                    >
                        {isLoading && "Loading Links..."}
                        {!isLoading && !!shareMethod && (
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="start"
                            >
                                <Box
                                    display="flex"
                                    alignItems="center"
                                >
                                    <IconButton
                                        onClick={(): void => {
                                            setShareMethod(null);
                                        }}
                                    >
                                        <ChevronLeft />
                                    </IconButton>
                                    <Box
                                        marginRight="16px"
                                    >
                                        {`Share ${shareMethod} Link`}
                                    </Box>
                                </Box>
                                <Box
                                    bgcolor="#00000022"
                                    paddingX="8px"
                                    paddingY="4px"
                                    borderRadius="9999px"
                                    marginX="16px"
                                >
                                    {`${shareMethod} View`}
                                </Box>
                                <MenuList
                                    sx={{
                                        width: "100%"
                                    }}
                                >
                                    <MenuItem
                                        onClick={copyLink}
                                    >
                                        <ListItemIcon>
                                            <ContentCopyOutlinedIcon fontSize="small" />
                                        </ListItemIcon>
                                        Copy Link
                                    </MenuItem>
                                    {/* @ts-ignore */}
                                    {navigator.share && (
                                        <MenuItem
                                            onClick={shareLink}
                                        >
                                            <ListItemIcon>
                                                <ShareOutlined fontSize="small" />
                                            </ListItemIcon>
                                            Share
                                        </MenuItem>
                                    )}
                                </MenuList>
                            </Box>
                        )}
                        {!isLoading && !shareMethod && (
                            <>
                                <MenuItem
                                    onClick={(): void => {
                                        setShareMethod(ShareMethod.PRIVATE);
                                    }}
                                >
                                    <ListItemIcon>
                                        <Lock fontSize="small" />
                                    </ListItemIcon>
                                    Private
                                </MenuItem>
                                <MenuItem
                                    onClick={(): void => {
                                        setShareMethod(ShareMethod.PUBLIC);
                                    }}
                                    disabled={!staticDashboardData?.metadata.isGenerated}
                                >
                                    <ListItemIcon>
                                        <Language fontSize="small" />
                                    </ListItemIcon>
                                    Public
                                </MenuItem>
                            </>
                        )}
                    </Box>
                </Menu>
            </Box>
        </>
    );
};
